import type { ViewRegistry, RegistryPolicy } from '../registry/types';
import type { NormalizedView } from '../types/normalized';
import { extractViewMetadataFromElement } from './dom-metadata';
import {
  isElementVisibleForViewTracking,
  resolveViewVisibilityMutationAttributeFilter,
} from './element-visibility';
import {
  findHenIdElements,
  resolveComponentIdFromTarget,
} from '../routing/resolve-component-id';
import {
  routeView,
  type UnknownComponentHandler,
} from '../routing/registry-router';

export interface ViewTrackerOptions {
  readonly root: Document | Element;
  readonly viewRegistry: ViewRegistry;
  readonly registryPolicy: RegistryPolicy;
  readonly onUnknownComponentId?: (componentId: string) => void;
  readonly onView: (view: NormalizedView) => void;
  readonly threshold?: number | number[];
  readonly rootMargin?: string;
  /**
   * When true, also re-evaluate on `class` / `style` attribute mutations (can be
   * noisy in React). Defaults to panel visibility attributes only
   * (`data-state`, `hidden`, `aria-hidden`).
   */
  readonly includeClassAndStyleVisibilityMutations?: boolean;
  /** Overrides the default / extended visibility mutation attribute filter. */
  readonly visibilityMutationAttributeFilter?: readonly string[];
}

const DEFAULT_THRESHOLD = 0.5;
const DEFAULT_ROOT_MARGIN = '0px';

function resolveMutationObserverRoot(root: Document | Element): Element {
  if (root instanceof Document) {
    return root.documentElement;
  }
  return root;
}

function isOpacityTransitionEnd(event: Event, target: Element): boolean {
  if (event.target !== target) {
    return false;
  }
  if (
    typeof TransitionEvent !== 'undefined' &&
    event instanceof TransitionEvent
  ) {
    return event.propertyName === 'opacity';
  }
  return event.type === 'transitionend';
}

export function startViewTracker(options: ViewTrackerOptions): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    return () => {};
  }

  const seenElements = new WeakSet<Element>();
  const observedElements = new WeakSet<Element>();
  const trackerTeardown = new AbortController();
  const pendingTransitionByTarget = new WeakMap<Element, AbortController>();
  const activeTransitionControllers = new Set<AbortController>();
  const visibilityMutationAttributeFilter =
    resolveViewVisibilityMutationAttributeFilter({
      includeClassAndStyleMutations:
        options.includeClassAndStyleVisibilityMutations,
      attributeFilter: options.visibilityMutationAttributeFilter,
    });
  const handler: UnknownComponentHandler = {
    policy: options.registryPolicy,
    onUnknownComponentId: options.onUnknownComponentId,
  };
  const tryEmitViewForElement = (element: Element): void => {
    if (trackerTeardown.signal.aborted) {
      return;
    }
    if (seenElements.has(element)) {
      return;
    }
    if (!isElementVisibleForViewTracking(element)) {
      return;
    }

    const componentId =
      element.getAttribute('data-hen-id') ??
      resolveComponentIdFromTarget(element);
    if (!componentId) {
      return;
    }

    const metadata = extractViewMetadataFromElement(element);
    const view = routeView(componentId, options.viewRegistry, handler, {
      metadata,
    });
    if (!view) {
      return;
    }

    seenElements.add(element);
    options.onView(view);
    observer.unobserve(element);
  };

  const processIntersectionEntries = (
    entries: IntersectionObserverEntry[],
  ): void => {
    for (const entry of entries) {
      if (!entry.isIntersecting) {
        continue;
      }
      tryEmitViewForElement(entry.target);
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      processIntersectionEntries(entries);
    },
    {
      root: options.root instanceof Document ? null : options.root,
      threshold: options.threshold ?? DEFAULT_THRESHOLD,
      rootMargin: options.rootMargin ?? DEFAULT_ROOT_MARGIN,
    },
  );

  const observeElement = (element: Element): void => {
    if (observedElements.has(element)) {
      return;
    }
    observedElements.add(element);
    observer.observe(element);
  };

  const observeHenIdSubtree = (subtreeRoot: Element): void => {
    for (const element of findHenIdElements(subtreeRoot)) {
      observeElement(element);
    }
  };

  const refreshIntersectionObservation = (element: Element): void => {
    if (!observedElements.has(element) || seenElements.has(element)) {
      return;
    }
    observer.unobserve(element);
    observer.observe(element);
    processIntersectionEntries(observer.takeRecords());
  };

  const reevaluateVisibilityForSubtree = (subtreeRoot: Element): void => {
    if (trackerTeardown.signal.aborted) {
      return;
    }

    const toRefresh: Element[] = [];
    for (const element of findHenIdElements(subtreeRoot)) {
      if (!observedElements.has(element) || seenElements.has(element)) {
        continue;
      }
      if (!isElementVisibleForViewTracking(element)) {
        continue;
      }
      toRefresh.push(element);
    }

    if (toRefresh.length === 0) {
      return;
    }

    const refreshAll = (): void => {
      if (trackerTeardown.signal.aborted) {
        return;
      }
      for (const element of toRefresh) {
        refreshIntersectionObservation(element);
      }
    };

    refreshAll();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(refreshAll);
      });
    }
  };

  const scheduleVisibilityReevaluation = (target: Element): void => {
    if (trackerTeardown.signal.aborted) {
      return;
    }

    reevaluateVisibilityForSubtree(target);

    const existingTransition = pendingTransitionByTarget.get(target);
    if (existingTransition) {
      existingTransition.abort();
      activeTransitionControllers.delete(existingTransition);
      pendingTransitionByTarget.delete(target);
    }

    const transitionListener = new AbortController();
    pendingTransitionByTarget.set(target, transitionListener);
    activeTransitionControllers.add(transitionListener);

    const onTransitionEnd: EventListener = (event): void => {
      if (trackerTeardown.signal.aborted || transitionListener.signal.aborted) {
        return;
      }
      if (!isOpacityTransitionEnd(event, target)) {
        return;
      }

      transitionListener.abort();
      activeTransitionControllers.delete(transitionListener);
      pendingTransitionByTarget.delete(target);
      reevaluateVisibilityForSubtree(target);
    };

    target.addEventListener('transitionend', onTransitionEnd, {
      signal: transitionListener.signal,
    });
  };

  for (const element of findHenIdElements(options.root)) {
    observeElement(element);
  }

  let mutationObserver: MutationObserver | undefined;
  if (typeof MutationObserver !== 'undefined') {
    mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node instanceof Element) {
              observeHenIdSubtree(node);
            }
          }
          continue;
        }

        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof Element
        ) {
          scheduleVisibilityReevaluation(mutation.target);
        }
      }
    });

    mutationObserver.observe(resolveMutationObserverRoot(options.root), {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: visibilityMutationAttributeFilter,
    });
  }

  return () => {
    trackerTeardown.abort();
    for (const controller of Array.from(activeTransitionControllers)) {
      controller.abort();
    }
    activeTransitionControllers.clear();
    mutationObserver?.disconnect();
    observer.disconnect();
  };
}
